import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { PublicUser } from '@bazaar/shared';
import { api } from '../lib/api.js';

interface AuthContextValue {
  user: PublicUser | null;
  status: 'loading' | 'ready';
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// A plain (non-HttpOnly) marker, set right after a successful login and
// cleared on logout - *not* itself a credential, just a hint. Every cold
// page load otherwise calls GET /api/auth/me to find out if the (HttpOnly)
// session cookie is still valid; when signed out, that's a guaranteed 401
// that Chrome logs to the console as a resource-load error regardless of
// the try/catch below, so "no console errors" was never quite true - see
// the QA report, P3 #21. Skipping the call entirely when this hint is
// absent (the common case: `/login` or any page, never having signed in
// on this browser) avoids that network round-trip and its console noise.
// If the hint is stale (cookie expired since), the request still happens
// and still 401s the first time something needs auth - just not on every
// single cold load of a browser that's never logged in at all.
const SESSION_HINT_KEY = 'bazaar.hadSession.v1';

function readSessionHint(): boolean {
  try {
    return localStorage.getItem(SESSION_HINT_KEY) === '1';
  } catch {
    return true; // Can't tell -> fail open to the normal /me check.
  }
}

function writeSessionHint(present: boolean): void {
  try {
    if (present) localStorage.setItem(SESSION_HINT_KEY, '1');
    else localStorage.removeItem(SESSION_HINT_KEY);
  } catch {
    // Private browsing / storage disabled - just skip the optimisation.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready'>('loading');

  useEffect(() => {
    if (!readSessionHint()) {
      setUser(null);
      setStatus('ready');
      return;
    }
    let cancelled = false;
    api
      .me()
      .then(({ user }) => {
        if (!cancelled) setUser(user);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
        writeSessionHint(false);
      })
      .finally(() => {
        if (!cancelled) setStatus('ready');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // When the tab regains focus, proactively check the session: a sales
  // person who leaves the tab open for a while (idle timeout, 2h) sees the
  // login screen (via the SESSION_EXPIRED handling in lib/api.ts) the
  // moment they come back, rather than a failed tap on whatever they try
  // first. Only checks when we currently think we're logged in.
  useEffect(() => {
    function handleFocus(): void {
      if (!user) return;
      api.me().catch(() => {
        // A real failure here is either SESSION_EXPIRED (already handled
        // globally by lib/api.ts's redirect) or a transient network error
        // (ignored - the next real request will surface it as usual).
      });
    }
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user]);

  async function login(username: string, password: string): Promise<void> {
    const { user } = await api.login({ username, password });
    writeSessionHint(true);
    setUser(user);
  }

  async function logout(): Promise<void> {
    try {
      await api.logout();
    } finally {
      writeSessionHint(false);
      setUser(null);
    }
  }

  return <AuthContext.Provider value={{ user, status, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
